package er.prateek.crossly.ui.components

import androidx.compose.animation.core.LinearEasing
import androidx.compose.animation.core.RepeatMode
import androidx.compose.animation.core.animateFloat
import androidx.compose.animation.core.infiniteRepeatable
import androidx.compose.animation.core.rememberInfiniteTransition
import androidx.compose.animation.core.tween
import androidx.compose.foundation.Canvas
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxScope
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.safeContentPadding
import androidx.compose.foundation.layout.widthIn
import androidx.compose.material3.MaterialTheme
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.unit.dp
import er.prateek.crossly.ads.AdPlacement
import er.prateek.crossly.ads.ui.AdBannerSlot
import er.prateek.crossly.ads.ui.rememberBannerReservedHeight
import er.prateek.crossly.ui.theme.game
import er.prateek.crossly.ui.theme.window
import kotlin.math.cos
import kotlin.math.sin

/**
 * The living backdrop every screen sits on: a palette gradient, two slowly
 * drifting color blobs, and a faint crossword lattice. It replaces the flat
 * `surface` fill that made the app read as a form rather than a game.
 */
@Composable
fun GameBackground(
    modifier: Modifier = Modifier,
    animated: Boolean = true,
    content: @Composable BoxScope.() -> Unit,
) {
    val colors = MaterialTheme.game
    val transition = rememberInfiniteTransition(label = "backdrop")
    // Two co-prime-ish periods so the blobs never settle into a visible loop.
    val driftA = if (animated) {
        transition.animateFloat(
            initialValue = 0f,
            targetValue = 1f,
            animationSpec = infiniteRepeatable(tween(19_000, easing = LinearEasing), RepeatMode.Reverse),
            label = "driftA",
        ).value
    } else {
        0.5f
    }
    val driftB = if (animated) {
        transition.animateFloat(
            initialValue = 0f,
            targetValue = 1f,
            animationSpec = infiniteRepeatable(tween(27_000, easing = LinearEasing), RepeatMode.Reverse),
            label = "driftB",
        ).value
    } else {
        0.5f
    }

    Box(modifier = modifier.fillMaxSize()) {
        Canvas(modifier = Modifier.fillMaxSize()) {
            drawRect(
                brush = Brush.verticalGradient(
                    listOf(colors.backdropTop, colors.backdropBottom),
                    startY = 0f,
                    endY = size.height,
                ),
            )

            val blobRadius = maxOf(size.width, size.height) * 0.6f
            val angleA = driftA * 2f * 3.14159f
            val angleB = driftB * 2f * 3.14159f
            drawCircle(
                brush = Brush.radialGradient(
                    colors = listOf(colors.glowA.copy(alpha = 0.55f), Color.Transparent),
                    center = Offset(
                        x = size.width * (0.18f + 0.12f * cos(angleA)),
                        y = size.height * (0.12f + 0.10f * sin(angleA)),
                    ),
                    radius = blobRadius,
                ),
                radius = blobRadius,
                center = Offset(
                    x = size.width * (0.18f + 0.12f * cos(angleA)),
                    y = size.height * (0.12f + 0.10f * sin(angleA)),
                ),
            )
            drawCircle(
                brush = Brush.radialGradient(
                    colors = listOf(colors.glowB.copy(alpha = 0.45f), Color.Transparent),
                    center = Offset(
                        x = size.width * (0.85f - 0.14f * sin(angleB)),
                        y = size.height * (0.82f + 0.10f * cos(angleB)),
                    ),
                    radius = blobRadius * 0.85f,
                ),
                radius = blobRadius * 0.85f,
                center = Offset(
                    x = size.width * (0.85f - 0.14f * sin(angleB)),
                    y = size.height * (0.82f + 0.10f * cos(angleB)),
                ),
            )

            // Crossword lattice: the theme motif, at whisper strength.
            val spacing = 56.dp.toPx()
            var x = 0f
            while (x <= size.width) {
                drawLine(colors.gridTexture, Offset(x, 0f), Offset(x, size.height), strokeWidth = 1f)
                x += spacing
            }
            var y = 0f
            while (y <= size.height) {
                drawLine(colors.gridTexture, Offset(0f, y), Offset(size.width, y), strokeWidth = 1f)
                y += spacing
            }
        }

        content()
    }
}

/**
 * Standard screen frame: backdrop, system-inset safety, and a content column
 * capped at the breakpoint's max width so a 2560px desktop window shows a
 * centred layout instead of a stretched phone one.
 */
@Composable
fun GameScreen(
    modifier: Modifier = Modifier,
    padded: Boolean = true,
    constrained: Boolean = true,
    /**
     * Opting this screen into a banner. Null means never; a non-null placement still only
     * shows one if Remote Config lists it and the user hasn't paid ads away, so passing a
     * placement is a permission rather than an instruction.
     */
    adPlacement: AdPlacement? = null,
    /**
     * Drawn edge-to-edge over the backdrop, outside the content padding and the
     * max-width clamp. Coach marks and the play countdown use this so their
     * scrim doesn't leave a bright frame of screen margin around a dimmed board.
     */
    overlay: @Composable BoxScope.() -> Unit = {},
    content: @Composable BoxScope.() -> Unit,
) {
    val window = MaterialTheme.window
    // Collapses to zero whenever no banner will be drawn, so an ad-free build lays out
    // exactly as it did before ads existed.
    val bannerHeight = rememberBannerReservedHeight(adPlacement)

    GameBackground(modifier = modifier) {
        Box(
            modifier = Modifier
                .fillMaxSize()
                .safeContentPadding()
                .padding(bottom = bannerHeight)
                .then(
                    if (padded) {
                        Modifier.padding(horizontal = window.screenPaddingHorizontal, vertical = window.screenPadding)
                    } else {
                        Modifier
                    },
                ),
            contentAlignment = Alignment.TopCenter,
        ) {
            Box(
                // widthIn caps the incoming constraint before fillMaxSize expands
                // into it — the reverse order would fill first and never clamp.
                modifier = if (constrained) {
                    Modifier.widthIn(max = window.contentMaxWidth).fillMaxSize()
                } else {
                    Modifier.fillMaxSize()
                },
                content = content,
            )
        }

        if (adPlacement != null) {
            AdBannerSlot(
                placement = adPlacement,
                modifier = Modifier.align(Alignment.BottomCenter).safeContentPadding(),
            )
        }

        overlay()
    }
}
